import { Building2, AlertTriangle, Wallet, Activity, RefreshCw } from 'lucide-react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useDashboardSummary, useDashboardTrend } from '@/hooks/useDashboard';
import { useSyncAll } from '@/hooks/useClients';
import { useSession } from '@/stores/session';
import { formatCurrency } from '@/lib/utils';

const PIE_COLORS = ['#5fbf80', '#e7a14a', '#e26464'];

export function DashboardPage() {
  const summary = useDashboardSummary();
  const trend = useDashboardTrend();
  const sync = useSyncAll();
  const isAdmin = useSession((s) => s.user?.role === 'admin');

  const s = summary.data;
  const cards = [
    {
      label: 'Total colegios',
      value: s ? s.total_clients.toLocaleString() : '—',
      icon: Building2,
      hint: 'Cacheado',
    },
    {
      label: 'Caducados',
      value: s ? s.caducados.toLocaleString() : '—',
      icon: AlertTriangle,
      hint: 'Acción urgente',
      tone: 'danger' as const,
    },
    {
      label: 'Total adeudado',
      value: s ? formatCurrency(s.total_adeudado) : '—',
      icon: Wallet,
      hint: 'Suma global',
    },
    {
      label: 'Pagos últimos 7d',
      value: s ? s.recent_payments.toLocaleString() : '—',
      icon: Activity,
      hint: 'client.pagar exitosos',
    },
  ];

  const pieData = s
    ? [
        { name: 'Al día', value: s.by_estado.al_dia },
        { name: 'En gracia', value: s.by_estado.en_gracia },
        { name: 'Caducado', value: s.by_estado.caducado },
      ]
    : [];

  return (
    <div className="space-y-8">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-moss-200">Dashboard</h1>
          <p className="mt-1 text-sm text-moss-300/70">
            Estado consolidado de cobranza. Cache se refresca automáticamente cada pocos minutos.
          </p>
        </div>
        {isAdmin && (
          <button
            type="button"
            className="btn-ghost"
            onClick={() => sync.mutate()}
            disabled={sync.isPending}
          >
            <RefreshCw className={`mr-1.5 h-4 w-4 ${sync.isPending ? 'animate-spin' : ''}`} />
            {sync.isPending ? 'Sincronizando…' : 'Forzar sync'}
          </button>
        )}
      </div>

      <section className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        {cards.map((c) => (
          <div key={c.label} className="glass-card p-5">
            <div className="flex items-start justify-between">
              <div>
                <div className="text-xs uppercase tracking-wide text-moss-300/70">{c.label}</div>
                <div className="mt-2 text-3xl font-semibold text-moss-200">{c.value}</div>
              </div>
              <div
                className={`rounded-xl p-2 ${c.tone === 'danger' ? 'bg-danger/15 text-danger' : 'bg-mustard-500/15 text-mustard-400'}`}
              >
                <c.icon className="h-5 w-5" />
              </div>
            </div>
            <div className="mt-3 text-xs text-moss-300/60">{c.hint}</div>
          </div>
        ))}
      </section>

      <section className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="glass-card p-5 lg:col-span-2">
          <div className="mb-2 flex items-center justify-between">
            <h2 className="text-base font-semibold text-moss-200">Pagos por mes (últimos 6)</h2>
            <span className="text-xs text-moss-300/60">client.pagar success</span>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={trend.data ?? []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1a4a41" />
                <XAxis dataKey="month" stroke="#9fc4b1" fontSize={12} />
                <YAxis stroke="#9fc4b1" fontSize={12} allowDecimals={false} />
                <Tooltip
                  contentStyle={{
                    background: '#10302a',
                    border: '1px solid #1a4a41',
                    borderRadius: 12,
                  }}
                  cursor={{ fill: 'rgba(231,195,74,0.06)' }}
                />
                <Bar dataKey="count" fill="#e7c34a" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="glass-card p-5">
          <div className="mb-2 flex items-center justify-between">
            <h2 className="text-base font-semibold text-moss-200">Distribución por estado</h2>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={pieData}
                  innerRadius={50}
                  outerRadius={90}
                  paddingAngle={3}
                  dataKey="value"
                >
                  {pieData.map((_, i) => (
                    <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    background: '#10302a',
                    border: '1px solid #1a4a41',
                    borderRadius: 12,
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-3 grid grid-cols-3 gap-2 text-center text-[11px]">
            {pieData.map((p, i) => (
              <div key={p.name}>
                <div
                  className="mx-auto h-1.5 w-6 rounded-full"
                  style={{ background: PIE_COLORS[i % PIE_COLORS.length] }}
                />
                <div className="mt-1 text-moss-300/80">{p.name}</div>
                <div className="font-semibold text-moss-200">{p.value}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
